import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Paper,
} from '@mui/material';
import { DocumentUpload } from './DocumentUpload';
import { DocumentManager } from './DocumentManager';
import { UploadResult } from '@nvgc/shared';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`document-tabpanel-${index}`}
      aria-labelledby={`document-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `document-tab-${index}`,
    'aria-controls': `document-tabpanel-${index}`,
  };
}

export const DocumentManagementPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const handleUploadComplete = (result: UploadResult) => {
    showNotification(
      `Document uploaded successfully and is being processed. Document ID: ${result.documentId}`,
      'success'
    );
    
    // Switch to the documents tab to show the uploaded document
    setActiveTab(1);
  };

  const handleUploadError = (error: string) => {
    showNotification(`Upload failed: ${error}`, 'error');
  };

  const handleDocumentDeleted = (_documentId: string) => {
    showNotification(`Document deleted successfully`, 'success');
  };

  const handleError = (error: string) => {
    showNotification(error, 'error');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Document Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload and manage your documents for AI-powered analysis and search.
        </Typography>
      </Box>

      <Paper elevation={1}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange} 
            aria-label="document management tabs"
          >
            <Tab label="Upload Documents" {...a11yProps(0)} />
            <Tab label="My Documents" {...a11yProps(1)} />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <Box sx={{ px: 3 }}>
            <Typography variant="h6" gutterBottom>
              Upload New Documents
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload PDF, DOCX, PNG, or JPG files to add them to your personal document collection.
              These documents will be processed and made searchable through the AI assistant.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>Supported formats:</strong> PDF, DOCX, PNG, JPG<br />
                <strong>Maximum file size:</strong> 10MB per file<br />
                <strong>Processing time:</strong> Usually 1-5 minutes depending on document size and complexity
              </Typography>
            </Alert>

            <DocumentUpload
              onUploadComplete={handleUploadComplete}
              onUploadError={handleUploadError}
            />
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <Box sx={{ px: 3 }}>
            <DocumentManager
              onDocumentDeleted={handleDocumentDeleted}
              onError={handleError}
            />
          </Box>
        </TabPanel>
      </Paper>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};